import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Grid,
  Stack,
  Paper
} from '@mui/material';

/**
 * Skeleton loader for KPI cards
 */
export const KPICardSkeleton: React.FC = () => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton width="30%" height={20} />
        </Box>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={36} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 1 }} />
      </Stack>
    </CardContent>
  </Card>
);

/**
 * Skeleton loader for chart containers
 */
export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <Paper sx={{ p: 3, height }}>
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between">
        <Skeleton variant="text" width="30%" height={32} />
        <Skeleton variant="rectangular" width={100} height={36} />
      </Box>
      <Skeleton variant="rectangular" width="100%" height={height - 100} />
    </Stack>
  </Paper>
);

/**
 * Skeleton loader for data tables
 */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Paper sx={{ p: 2 }}>
    <Stack spacing={1}>
      {/* Header */}
      <Box display="flex" gap={2} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
        {[1, 2, 3, 4].map((col) => (
          <Skeleton key={col} variant="text" width={`${100/4}%`} height={24} />
        ))}
      </Box>
      {/* Rows */}
      {[...Array(rows)].map((_, index) => (
        <Box key={index} display="flex" gap={2} sx={{ py: 1 }}>
          {[1, 2, 3, 4].map((col) => (
            <Skeleton key={col} variant="text" width={`${100/4}%`} height={20} />
          ))}
        </Box>
      ))}
    </Stack>
  </Paper>
);

/**
 * Skeleton loader for filter dropdowns
 */
export const FilterSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
    {[1, 2, 3, 4].map((filter) => (
      <Skeleton key={filter} variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
    ))}
  </Box>
);

/**
 * Full dashboard skeleton
 */
export const DashboardSkeleton: React.FC = () => (
  <Box sx={{ p: 3 }}>
    {/* Filters */}
    <FilterSkeleton />

    {/* KPI Cards */}
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map((card) => (
        <Grid item xs={12} sm={6} md={3} key={card}>
          <KPICardSkeleton />
        </Grid>
      ))}
    </Grid>

    {/* Charts */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <ChartSkeleton height={350} />
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartSkeleton height={350} />
      </Grid>
      <Grid item xs={12}>
        <TableSkeleton rows={8} />
      </Grid>
    </Grid>
  </Box>
);

/**
 * Product card skeleton for inventory views
 */
export const ProductCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Stack spacing={1.5}>
        <Box display="flex" justifyContent="space-between">
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
        </Box>
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="80%" />
        <Box display="flex" gap={1} mt={2}>
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/**
 * List item skeleton
 */
export const ListItemSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: 1, borderColor: 'divider' }}>
    <Skeleton variant="circular" width={48} height={48} />
    <Box flex={1}>
      <Skeleton variant="text" width="30%" height={24} />
      <Skeleton variant="text" width="50%" height={20} />
    </Box>
    <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
  </Box>
);